/**
 * Carries the address from a signup email that already has an account to the
 * landing sign-in form or the lost-password form, which open with it filled.
 *
 * The address goes through sessionStorage and never through the URL, so it
 * stays out of access logs, Referer headers and analytics page URLs.
 */

import { safeStorageGet, safeStorageRemove, safeStorageSet, SESSION_STORAGE } from './safe-storage.js';

const HANDOFF_KEY = 'ip/account-email-handoff';

/**
 * @param {string} email - the address the signup wizard refused as taken
 * @returns {boolean} whether the address was stored
 */
export function stashAccountEmail(email) {
    const value = String(email || '').trim();

    return value !== '' && safeStorageSet(HANDOFF_KEY, value, SESSION_STORAGE);
}

// The landing sign-in form carries the field only while its view shows (?view=signin).
function handoffTarget(doc) {
    const field = Array.from(doc.querySelectorAll('input[data-account-email]'))
        .find((input) => input.getClientRects().length > 0);
    if (!field) {
        return null;
    }

    return { form: field.form, field, next: field.form?.querySelector('[data-account-email-next]') || null };
}

/**
 * Fill the form on this page, if it has one, and drop the stored address.
 * A page without either form leaves the address for the next page in the tab.
 *
 * @param {Document} doc
 * @returns {boolean} whether a form took the address
 */
export function applyAccountEmailHandoff(doc) {
    const email = safeStorageGet(HANDOFF_KEY, SESSION_STORAGE);
    if (!email) {
        return false;
    }

    const target = handoffTarget(doc);
    if (!target) {
        return false;
    }

    safeStorageRemove(HANDOFF_KEY, SESSION_STORAGE);
    // A value the server rendered, such as the name of a failed sign-in, stays.
    if (target.field.value === '') {
        target.field.value = email;
    }

    if (target.next) {
        // On a phone the landing sign-in form sits below the hero.
        target.form.scrollIntoView({ block: 'center' });
        target.next.focus({ preventScroll: true });
    }

    return true;
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => applyAccountEmailHandoff(document));
    } else {
        applyAccountEmailHandoff(document);
    }
}
